import { Fragment, useEffect, useMemo, useState } from 'react';
import { apiFetch, parseJsonSafe } from '@/lib/api.js';
import { useParams } from 'react-router-dom';

const fmt = (n) => {
  if (n == null) return '—';
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(n >= 1e4 ? 0 : 1)}K`;
  return String(n);
};

// "how long pending" from a first-seen timestamp.
function pendingAge(iso) {
  if (!iso) return null;
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 60) return `${days}d`;
  return `${Math.floor(days / 30)}mo`;
}

const SUBTYPE_LABEL = {
  ENGAGEMENT: 'FB Engagement', IG_BUSINESS: 'IG Engagement', LOOKALIKE: 'Lookalike',
  WEBSITE: 'Website', VIDEO: 'Video', CUSTOM: 'Custom',
};
const subtypeLabel = (s) => (s ? (SUBTYPE_LABEL[s] || s) : '—');

// Inline detail for a page: the custom audiences built from it (FB engagement
// matches page.id, IG matches the page's IG account).
function PageAudiences({ audiences }) {
  if (!audiences || audiences.length === 0) {
    return <div className="text-xs text-muted-foreground">No audiences built from this page.</div>;
  }
  return (
    <div className="space-y-1">
      {audiences.map((a) => (
        <div key={a.audience_id} className="flex items-center gap-2 text-xs">
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground whitespace-nowrap">{subtypeLabel(a.subtype)}</span>
          <span className="flex-1 truncate">{a.audience_name || a.artist}</span>
          <span className="tabular-nums text-muted-foreground whitespace-nowrap">{fmt(a.approx_count_upper ?? a.approx_count_lower)}</span>
          <span className="font-mono text-[10px] text-muted-foreground/70 whitespace-nowrap">{a.audience_id}</span>
        </div>
      ))}
    </div>
  );
}

export default function FacebookPages() {
  const { tenantSlug } = useParams();
  const [data, setData] = useState({ pages: [], count: 0, owned: 0, client: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [access, setAccess] = useState('all'); // all | owned | client
  const [showRequest, setShowRequest] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const toggleExpand = (id) => setExpanded((s) => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });

  const load = (force) => {
    setLoading(true);
    setError(null);
    apiFetch(`/api/tenants/${tenantSlug}/facebook-pages${force ? '?refresh=1' : ''}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.error) throw new Error(d.detail || d.error);
        setData(d);
        setLoading(false);
      })
      .catch((e) => { setError(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, [tenantSlug]);

  const visible = useMemo(() => data.pages.filter((p) => {
    if (access !== 'all' && p.access !== access) return false;
    if (filter) {
      const q = filter.toLowerCase();
      if (!(`${p.name || ''} ${p.category || ''} ${p.instagram || ''} ${p.id}`.toLowerCase().includes(q))) return false;
    }
    return true;
  }), [data.pages, access, filter]);

  if (loading) return <div className="text-muted-foreground">Loading…</div>;
  if (error) return <div className="text-destructive">Error: {error}</div>;

  const AccessTab = ({ k, label, n }) => (
    <button
      onClick={() => setAccess(k)}
      className={`px-2.5 py-1 text-xs rounded-md border ${access === k ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-input hover:bg-accent'}`}
    >{label} {n != null && <span className="opacity-70">({n})</span>}</button>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Pages</h1>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowRequest(true)} className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded-md hover:opacity-90" title="Request advertising access to an artist's Page">+ Request ad access</button>
          <button onClick={() => load(true)} className="px-3 py-1 text-xs border border-input rounded-md hover:bg-accent" title="Re-fetch the live list from Meta (takes a few seconds)">↻ Refresh</button>
          <div className="text-sm text-muted-foreground">
            {visible.length} of {data.count}
            {data.cache_age_ms != null && <span className="ml-1 text-muted-foreground/60">· {data.cache_age_ms < 60000 ? 'just now' : `${Math.round(data.cache_age_ms / 60000)}m ago`}</span>}
          </div>
        </div>
      </div>

      <div className="text-xs text-muted-foreground mb-3">
        Facebook Pages the business has access to (owned + shared), live from Meta, matches Business Settings → Pages.
      </div>

      {data.pending?.length > 0 && (
        <div className="mb-3 p-2.5 rounded-md border border-amber-500/40 bg-amber-500/10 text-xs">
          <div className="font-medium text-amber-700 dark:text-amber-400 mb-1.5">
            {data.pending.length} pending access request{data.pending.length === 1 ? '' : 's'}, awaiting the Page owner's approval
          </div>
          <div className="space-y-1">
            {data.pending.map((p) => (
              <div key={p.id} className="flex items-center gap-2">
                <span className="font-medium">{p.name || 'Unknown page'}</span>
                <span className="font-mono text-[11px] text-muted-foreground">{p.id}</span>
                {pendingAge(p.first_seen_at) && (
                  <span className="text-[11px] text-amber-700/80 dark:text-amber-400/80" title={`Pending since ${new Date(p.first_seen_at).toLocaleString()}`}>
                    pending {pendingAge(p.first_seen_at)}
                  </span>
                )}
                <div className="flex-1" />
                <button
                  onClick={() => setCancelTarget(p)}
                  className="px-2 py-0.5 border border-input rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
                >Cancel</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <input
          type="text"
          placeholder="Filter by name, category, IG, or ID…"
          className="flex-1 min-w-[12rem] px-3 py-1.5 rounded-md border border-input bg-background text-sm"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <div className="flex gap-1.5">
          <AccessTab k="all" label="All" n={data.count} />
          <AccessTab k="owned" label="Owned" n={data.owned} />
          <AccessTab k="client" label="Shared" n={data.client} />
        </div>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {visible.map((p) => {
          const isOpen = expanded.has(p.id);
          const count = p.audience_count ?? (p.audiences?.length || 0);
          return (
          <div key={p.id} className="border border-border rounded-md p-3 bg-card text-sm">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => toggleExpand(p.id)}>
              {p.picture ? <img src={p.picture} alt="" referrerPolicy="no-referrer" className="w-10 h-10 rounded-full object-cover bg-muted flex-shrink-0" /> : <div className="w-10 h-10 rounded-full bg-muted flex-shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1 font-medium truncate">
                  {p.name}{p.verified && <span title="Verified" className="text-primary">✓</span>}
                </div>
                <div className="text-xs text-muted-foreground truncate">{p.category || '—'} · {fmt(p.followers)} followers</div>
                {p.instagram && <div className="text-[11px] text-muted-foreground">IG @{p.instagram}{p.instagram_followers != null ? ` · ${fmt(p.instagram_followers)}` : ''}</div>}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{count} aud</span>}
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${p.access === 'owned' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' : 'bg-muted text-muted-foreground'}`}>{p.access === 'owned' ? 'Owned' : 'Shared'}</span>
                <span className="text-muted-foreground">{isOpen ? '▾' : '▸'}</span>
              </div>
            </div>
            {isOpen && (
              <div className="mt-2 border-t border-border pt-2">
                <PageAudiences audiences={p.audiences} />
              </div>
            )}
          </div>
          );
        })}
        {visible.length === 0 && <div className="text-center text-muted-foreground py-6 text-sm">No pages match.</div>}
      </div>

      {/* Desktop table */}
      <div className="border border-border rounded-md overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-2 py-2 font-medium w-6"></th>
              <th className="px-3 py-2 font-medium w-12"></th>
              <th className="px-3 py-2 font-medium">Page</th>
              <th className="px-3 py-2 font-medium">Category</th>
              <th className="px-3 py-2 font-medium text-right">Followers</th>
              <th className="px-3 py-2 font-medium">Instagram</th>
              <th className="px-3 py-2 font-medium">Access</th>
              <th className="px-3 py-2 font-medium text-right">Audiences</th>
              <th className="px-3 py-2 font-medium">ID</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((p) => {
              const isOpen = expanded.has(p.id);
              const count = p.audience_count ?? (p.audiences?.length || 0);
              return (
              <Fragment key={p.id}>
              <tr className="border-t border-border hover:bg-accent/30 cursor-pointer" onClick={() => toggleExpand(p.id)}>
                <td className="px-2 py-2 text-muted-foreground">{isOpen ? '▾' : '▸'}</td>
                <td className="px-3 py-1.5">
                  {p.picture ? <img src={p.picture} alt="" referrerPolicy="no-referrer" className="w-8 h-8 rounded-full object-cover bg-muted" /> : <div className="w-8 h-8 rounded-full bg-muted" />}
                </td>
                <td className="px-3 py-2">
                  <a href={p.link || `https://facebook.com/${p.id}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-primary hover:underline inline-flex items-center gap-1">
                    {p.name}{p.verified && <span title="Verified">✓</span>}
                  </a>
                </td>
                <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{p.category || '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmt(p.followers)}</td>
                <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{p.instagram ? (
                  <span className="inline-flex items-center gap-1.5">
                    <a href={`https://instagram.com/${p.instagram}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="hover:underline">@{p.instagram}</a>
                    {p.instagram_followers != null && <span className="text-xs text-muted-foreground/70 tabular-nums">{fmt(p.instagram_followers)}</span>}
                  </span>
                ) : '—'}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${p.access === 'owned' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' : 'bg-muted text-muted-foreground'}`}>{p.access === 'owned' ? 'Owned' : 'Shared'}</span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{count > 0 ? count : <span className="text-muted-foreground">0</span>}</td>
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{p.id}</td>
              </tr>
              {isOpen && (
                <tr className="bg-muted/20 border-t border-border">
                  <td colSpan={9} className="px-4 py-3"><PageAudiences audiences={p.audiences} /></td>
                </tr>
              )}
              </Fragment>
              );
            })}
            {visible.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">No pages match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showRequest && (
        <RequestAccessDialog
          tenantSlug={tenantSlug}
          onClose={() => setShowRequest(false)}
          onDone={() => { setShowRequest(false); load(); }}
        />
      )}

      {cancelTarget && (
        <CancelRequestDialog
          tenantSlug={tenantSlug}
          target={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onDone={() => { setCancelTarget(null); load(true); }}
        />
      )}
    </div>
  );
}

function CancelRequestDialog({ tenantSlug, target, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/facebook-pages/cancel-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page_id: target.id }),
      });
      const d = await parseJsonSafe(res);
      if (!res.ok) { setError(d.upstream || d.detail || d.error); setBusy(false); return; }
      onDone();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border border-border rounded-md max-w-sm w-full p-4" onClick={(e) => e.stopPropagation()}>
        <div className="font-medium mb-2">Cancel access request?</div>
        <div className="text-sm text-muted-foreground">
          Withdraw the pending access request for <span className="font-medium text-foreground">{target.name || 'this page'}</span>
          <span className="font-mono text-xs"> ({target.id})</span>? You can request it again later.
        </div>
        {error && <div className="mt-2 text-xs text-destructive">✗ {error}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} disabled={busy} className="px-3 py-1 text-sm border border-input rounded-md hover:bg-accent disabled:opacity-50">Keep</button>
          <button onClick={confirm} disabled={busy} className="px-3 py-1 text-sm border border-destructive text-destructive rounded-md hover:bg-destructive/10 disabled:opacity-50">
            {busy ? 'Cancelling…' : 'Cancel request'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RequestAccessDialog({ tenantSlug, onClose, onDone }) {
  const [page, setPage] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!page.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/facebook-pages/request-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page: page.trim(), email: email.trim() }),
      });
      const d = await parseJsonSafe(res);
      if (!res.ok) setResult({ ok: false, msg: d.detail || d.upstream || d.error });
      else setResult({
        ok: true,
        msg: `Request sent for ${d.page_name || d.page_id}. It's pending the Page owner's approval.`,
        email: d.email || null,
      });
    } catch (e) {
      setResult({ ok: false, msg: e.message });
    } finally {
      setBusy(false);
    }
  };

  const mailtoHref = (em) => `mailto:${encodeURIComponent(em.to)}?subject=${encodeURIComponent(em.subject)}&body=${encodeURIComponent(em.body)}`;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border border-border rounded-md max-w-md w-full p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-medium">Request ad access to a Page</div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>
        <div className="text-xs text-muted-foreground mb-2">
          Paste the artist's <span className="font-medium">Facebook Page URL</span> (e.g. <code>facebook.com/davidguetta</code>) or numeric Page ID. We'll resolve the Page, then request <span className="font-medium">Advertise</span> + <span className="font-medium">Analyze</span> access, the Page owner approves on their side, same as Business Manager.
          <span className="block mt-1 text-muted-foreground/80">Resolving a page URL can take a few seconds.</span>
        </div>
        <input
          type="text"
          autoFocus
          value={page}
          onChange={(e) => setPage(e.target.value)}
          placeholder="facebook.com/davidguetta  or  7619396355"
          className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm"
        />

        <label className="block text-xs text-muted-foreground mt-3 mb-1">Management team email <span className="text-muted-foreground/60">(optional)</span></label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="management@artist.com"
          className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm"
        />
        <div className="text-[11px] text-muted-foreground/80 mt-1">If set, we'll prepare an approval-instructions email to send them.</div>

        {result && (
          <div className={`mt-3 text-xs ${result.ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-destructive'}`}>
            {result.ok ? '✓ ' : '✗ '}{result.msg}
          </div>
        )}
        {result?.ok && result.email && (
          <div className="mt-2 border border-border rounded-md p-2.5 bg-muted/30 text-xs">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="font-medium">Approval email → {result.email.to}</span>
              <a
                href={mailtoHref(result.email)}
                className="px-2 py-0.5 border border-input rounded-md hover:bg-accent whitespace-nowrap"
              >Open in email app</a>
            </div>
            <div className="text-muted-foreground"><span className="font-medium">Subject:</span> {result.email.subject}</div>
            <pre className="mt-1 whitespace-pre-wrap text-[11px] text-muted-foreground max-h-40 overflow-y-auto">{result.email.body}</pre>
            {!result.email.delivered && (
              <div className="mt-1 text-amber-700 dark:text-amber-400">Auto-send isn't configured yet. Use “Open in email app” to send it now.</div>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1 text-sm border border-input rounded-md hover:bg-accent">Close</button>
          <button
            onClick={result?.ok ? onDone : submit}
            disabled={busy || (!result?.ok && !page.trim())}
            className="px-3 py-1 text-sm bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50"
          >{busy ? 'Requesting…' : result?.ok ? 'Done' : 'Request access'}</button>
        </div>
      </div>
    </div>
  );
}
